import { useLazyQuery, useMutation } from "@apollo/client";
import { Box, Divider, Grid } from "@mui/material";
import {
  FETCH_SELECTED_COLLECTION_ITEM_DETAIL,
  FETCH_TAG_LIST_QUERY,
  commentsApi,
  contentTypeAPIs,
  getScheduleErrorText,
  useComment,
  useSlugValidation,
  useWorkflow,
} from "@platformx/authoring-apis";
import { RootState, previewContent } from "@platformx/authoring-state";
import { CommentListPanel, CommentWrapper } from "@platformx/comment-review";
import { ErrorMessageHandle } from "@platformx/schema-based-content-type";
import { buildTagSmEntry, dedupeTagSmEntries, findTagSmEntryIndex } from "@platformx/tag-selector";
import {
  AILoader,
  AUTH_INFO,
  AutoTextArea,
  CATEGORY_CONTENT,
  CommonBoxWithNumber,
  CommonPlateformXDialog,
  ContentPublishPlan,
  EMPTY_SCHEDULE,
  Loader,
  MAPPING,
  SchedulePublishUnpublish,
  ScheduleValue,
  ShowToastError,
  ShowToastSuccess,
  TextBox,
  TitleSubTitle,
  appendBrandNameIfPresent,
  applyPublishStep,
  capitalizeFirstLetter,
  formatPageUrl,
  toAbsoluteUrl,
  getCurrentLang,
  getSelectedBrandName,
  getTitleMax,
  mergeAutoStructureData,
  PendingSchedule,
  planContentPublish,
  runPublishSteps,
  scheduleFromTriggers,
  useAccess,
  useUserSession,
  workflowKeys,
} from "@platformx/utilities";
import { WorkflowHistory } from "@platformx/workflow-management";
import { DamContentGallery, XImageRender } from "@platformx/x-image-render";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  brandFieldsFromDoc,
  brandFieldsToSave,
  resolveCanonicalPath,
} from "../../../utils/canonicalPath";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AuthorToggle from "../../../components/AdditionalSettings/AuthorToggle";
import Analytics from "../../../components/Analytics/Analytics";
import ContentPageScroll from "../../../components/ContentPageScroll";
import ContentStatus from "../../../components/ContentStatus/ContentStatus";
import { CreateHeader } from "../../../components/CreateHeader/CreateHeader";
import FCMNotification from "../../../components/FCMNotification/FCMNotification";
import Seo from "../../../components/SEO/SEO";
import SocialShare from "../../../components/SocialShare/SocialShare";
import { TagSelectorPanel } from "../../../components/TagSelectorPanel/TagSelectorPanel";
import { ContentType } from "../../../enums/ContentType";
import useAIContent from "../../../hooks/useAIcontent/useAIcontent";
import { CONTENT_STATUS_PERMISSION, NOTIFICATION_PERMISSION } from "../../../utils/Constants";
import {
  contentStatusTitleSubTitles,
  syncTagSm,
  notificationTitleSubTitles,
} from "../../../utils/Helper";
import { resolveSlugDocType } from "../../../utils/slugDocType";
import icons, {
  DEF_VOD,
  analyticsTitleSubTitles,
  authorTitleSubTitles,
  defaultVodRef,
  seoTitleSubTitles,
  socialShareTitleSubTitles,
} from "./Utils/constats";
import {
  fieldValidationForPublish,
  getNewVod,
  getUpdatedVodToSend,
  getVodToSend,
  removeVODDuplicateTag,
  updateStructureData,
  updateVodSettings,
} from "./Utils/helper";
import { resolveVideoUrl, toRelativeVideoPath } from "./Utils/videoPath";
import { ChooseVideoTray } from "./components/chooseVideoTray/ChooseVideoTray";
import "./createVod.css";
import { useStyles } from "./createVod.styles";

type CreateVodProps = {
  isShared?: boolean;
};

export const CreateVod = ({ isShared = false }: CreateVodProps) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const updateTempObj = useRef<any>({});
  const { currentContent } = useSelector((state: RootState) => state.content);
  const [getSession] = useUserSession();
  const { userInfo, role } = getSession();
  const login_user_id = userInfo?.user_id;
  const username = `${userInfo.first_name} ${userInfo.last_name}`;
  const vodPageUrl = new URL(window.location.href);
  const { getWorkflowDetails, workflowRequest } = useWorkflow();
  const galleryType = useRef<string>("Video");
  const [isLoading, setIsLoading] = useState(false);
  const [enableWorkflowHistory, setEnableWorkflowHistory] = useState<boolean>(false);
  const [workflowStatus, setWorkflowStatus] = useState(true);
  const [showWorkflowSubmit, setShowWorkflowSubmit] = useState(false);
  const [pageStatus, setPageStatus] = useState("DRAFT");
  const [isFeatured, setIsFeatured] = useState(false);
  const [isDraft, setIsDraft] = useState<boolean>(true);
  const [draftPageURL, setDraftPageURL] = useState<string>("");
  const [parentToolTip, setParentToolTip] = useState("");
  const [runFetchTagList] = useLazyQuery(FETCH_TAG_LIST_QUERY);
  const currentVodData = useRef(
    vodPageUrl.searchParams.get("path") ? (vodPageUrl.searchParams.get("path") as string) : "",
  );
  const [createVodMutate] = useMutation(contentTypeAPIs.createContentType);
  const [updateVodMutate] = useMutation(contentTypeAPIs.updateContentType);
  const [publishVodMutate] = useMutation(contentTypeAPIs.publishContentType);
  const [fetchVodQuery] = useLazyQuery(contentTypeAPIs.fetchContentByPath);
  const slugValidation = useSlugValidation(resolveSlugDocType("vod"));
  const originalSlugRef = useRef<string>("");
  const [openPageExistModal, setOpenPageExistModal] = useState<boolean>(false);

  const [vodTagsHydrated, setVodTagsHydrated] = useState<boolean>(
    !vodPageUrl.searchParams.get("path") || Object.keys(currentContent).length > 0,
  );
  const [tagData, setTagData] = useState<any>([]);
  const [tagCategory, setTagCategory] = useState<any>([]);

  const [vodInstance, setVodInstance] = useState<any>({});
  const [workflow, setWorkflow] = useState({});
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  // KIWI-6576. Form state only, applied when the editor publishes; a draft save does not persist it.
  const [schedule, setSchedule] = useState<ScheduleValue>(EMPTY_SCHEDULE);
  // KIWI-6762. The schedules the item already had when opened: shown locked, never sent again.
  const initialSchedule = useRef<ScheduleValue>(EMPTY_SCHEDULE);
  const [pendingSchedule, setPendingSchedule] = useState<PendingSchedule>();
  const [scheduleErrors, setScheduleErrors] = useState<{ publish?: string; unpublish?: string }>(
    {},
  );
  const [scheduleConfirmation, setScheduleConfirmation] = useState("");
  const [showExitWarning, setShowExitWarning] = useState(false);
  const unsavedChanges = useRef<boolean>(currentContent.isUnsavedVod);
  const [galleryState, setGalleryState] = useState<boolean>(false);
  const vodRef = useRef<any>(defaultVodRef(currentContent) || DEF_VOD);
  const tagRef = useRef<any>([]);
  const scrollDebounceRef = useRef<any>(null);
  const [previewButton, setPreviewButton] = useState(true);
  const [previewTooltip, setPreviewTooltip] = useState(true);
  const { comments } = useComment();
  const [isError, setIsError] = useState<any>([]);
  const { fetchContent, loading } = useAIContent(FETCH_SELECTED_COLLECTION_ITEM_DETAIL);
  const [openAImodal, setOpenAImodal] = useState(false);
  const aiContentRef = useRef<any>();
  const { canAccessAction } = useAccess();
  /* Notification settings are driven by the logged-in user's session permissions */
  const hasNotificationAccess = canAccessAction(
    NOTIFICATION_PERMISSION.category,
    NOTIFICATION_PERMISSION.subCategory,
    NOTIFICATION_PERMISSION.action,
  );
  /* Content status is driven by the logged-in user's session permissions */
  const hasContentStatusAccess = canAccessAction(
    CONTENT_STATUS_PERMISSION.category,
    CONTENT_STATUS_PERMISSION.subCategory,
    CONTENT_STATUS_PERMISSION.action,
  );

  const workflowSubmitRequest = async (workflowObj, status) => {
    try {
      // Perform the workflow request
      setIsLoading(true);
      const { success, workflow_status } = await workflowRequest(workflowObj, status);
      if (success) {
        workflow_status === workflowKeys.publish.toLowerCase() && status === workflowKeys.approve
          ? setShowPublishConfirm(true)
          : setShowWorkflowSubmit(true);
      }
    } catch (error: any) {
      // Handle the error
      const statusCode = error?.networkError?.statusCode || 0;
      if (error?.graphQLErrors?.length > 0) {
        ShowToastError(error?.graphQLErrors[0]?.message);
      } else {
        ShowToastError(t("api_error_toast"), statusCode);
      }
    } finally {
      setIsLoading(false);
    }
  };
  const updateField = (updatedCommonFields, updatedObjectFields, callPreview = false) => {
    updateTempObj.current = {
      CommonFields: { ...updateTempObj.current?.CommonFields, ...updatedCommonFields },
      ObjectFields: { ...updateTempObj.current?.ObjectFields, ...updatedObjectFields },
    };
    const currentVodInstance = structuredClone(vodInstance);
    setVodInstance((prevState) => {
      return {
        ...prevState,
        CommonFields: {
          ...prevState.CommonFields,
          ...updatedCommonFields,
          modificationDate: new Date().toISOString(),
        },
        ObjectFields: {
          ...prevState.ObjectFields,
          ...updatedObjectFields,
        },
      };
    });
    if (callPreview) {
      dispatch(
        previewContent({
          ...currentVodInstance,
          CommonFields: {
            ...currentVodInstance.CommonFields,
            ...updatedCommonFields,
            modificationDate: new Date().toISOString(),
            contentType: "Vod",
            isDraft: isDraft,
            draftPageURL: draftPageURL,
          },
          ObjectFields: {
            ...currentVodInstance.ObjectFields,
            ...updatedObjectFields,
          },
          // Preview round-trip only (kept out of CommonFields so it is never saved): the existing
          // schedule must still be shown locked when the editor comes back (KIWI-6762).
          scheduleTriggers: {
            scheduled_publish_trigger_datetime: pendingSchedule?.publish ?? null,
            scheduled_unPublish_trigger_datetime: pendingSchedule?.unpublish ?? null,
          },
        }),
      );
      navigate("/content/preview", {
        state: { contentType: MAPPING.Vod, pageName: currentVodData?.current || draftPageURL },
      });
    }
  };

  /**
   * thumbnail update
   */
  const updateImageField = (updatedPartialObj) => {
    const selectedOriginalImage = updatedPartialObj?.original_image || {};
    const selectedPublishedImages = updatedPartialObj?.published_images || [];
    const partialPath = selectedOriginalImage?.Thumbnail || "";

    vodRef.current.imageSocialShare = partialPath;
    vodRef.current.notification_image = partialPath;
    vodRef.current.original_image = selectedOriginalImage;
    vodRef.current.published_images = selectedPublishedImages;
    const structureData = updateStructureData(vodRef);
    setVodInstance((prevState) => {
      return {
        ...prevState,
        CommonFields: {
          ...prevState.CommonFields,
          modificationDate: new Date().toISOString(),
          imageSocialShare: partialPath,
          notification_image: partialPath,
          structure_data: mergeAutoStructureData(
            prevState?.CommonFields?.structure_data,
            structureData,
          ),
        },
        ObjectFields: {
          ...prevState.ObjectFields,
          original_image: selectedOriginalImage,
          published_images: selectedPublishedImages,
        },
      };
    });
    setIsError({ ...isError, Thumbnail: false });
  };

  const updateCurrentInstance = (pageURL, callPreview = false) => {
    const videoTags = vodRef?.current?.Tags || tagRef.current;
    const videoTagSm = dedupeTagSmEntries(vodRef?.current?.tag_sm || tagCategory);
    const updatedCommonFields = {
      page: vodRef?.current?.Page || "",
      title: vodRef?.current?.title || "",
      description: vodRef?.current?.description || "",
      tags: removeVODDuplicateTag(videoTags),
      tag_sm: videoTagSm,
      current_page_url: `/${pageURL}`,
      canonical_url: resolveCanonicalPath({
        slug: vodRef?.current?.slug || vodInstance?.CommonFields?.slug,
        language: i18n.language,
        tagName: "vod",
        brandId: vodRef?.current?.brand_name,
        stored: vodRef?.current?.storedCanonical,
      }),
      ...brandFieldsToSave(vodRef?.current),
      settings: {
        ...updateVodSettings(
          vodRef,
          currentVodData,
          formatPageUrl(getSelectedBrandName() || i18n.language),
        ),
      },
      is_featured: isFeatured,
      analytics_enable: vodRef.current.analytics_enable,
      seo_enable: vodRef.current.seo_enable,
      ...(callPreview ? { workflow } : {}),
      // Add FCM notification fields from root of vodInstance
      notification: vodInstance?.CommonFields?.notification || false,
      notification_title:
        vodInstance?.CommonFields?.notification_title || vodInstance?.CommonFields?.title || "",
      notification_desc:
        vodInstance?.CommonFields?.notification_desc ||
        vodInstance?.CommonFields?.description ||
        "",
      notification_image: vodInstance?.CommonFields?.notification_image,
      notification_channel: vodInstance?.CommonFields?.notification_channel || [],
      notification_topics: vodInstance?.CommonFields?.notification_topics || [],
      content_behind_rendering: vodInstance?.CommonFields?.content_behind_rendering || "",
      structure_data: mergeAutoStructureData(
        vodInstance?.CommonFields?.structure_data,
        updateStructureData(vodRef),
      ),
    };
    const updatedObjectFields = {
      original_image: vodRef?.current?.original_image || {},
      published_images: vodRef?.current?.published_images || [],
      DsapceVideoUrl: vodRef?.current?.DsapceVideoUrl || "",
      VodVideoId: vodRef?.current?.VodVideoId || "",
    };

    updateField(updatedCommonFields, updatedObjectFields, callPreview);
  };

  const handelPreview = () => {
    if (!currentVodData.current) {
      updateCurrentInstance(
        vodRef?.current?.title.replace(/[^A-Z0-9]+/gi, "-").toLowerCase(),
        true,
      );
    } else {
      updateCurrentInstance(currentVodData.current, true);
    }
  };

  const exitWithoutSave = () => {
    setShowExitWarning(false);
    navigate("/content/vod");
  };

  /**
   * Publishes the VOD that was just saved. With no schedule this is the request that was always
   * sent. With one, each step is its own publish call, in order; any failure rejects, so a schedule
   * that did not take effect never looks like a success.
   */
  const publishVod = async (page: string, plan?: ContentPublishPlan) => {
    if (!plan?.isScheduled) {
      await publishVodMutate({ variables: { contentType: "Vod", input: { page } } });
      setScheduleConfirmation("");
    } else {
      await runPublishSteps(plan.steps, (step) =>
        publishVodMutate({
          variables: { contentType: "Vod", input: applyPublishStep({ page }, step) },
        }),
      );
      setScheduleConfirmation(
        plan.confirmation ? t(plan.confirmation.key, plan.confirmation.params) : "",
      );
    }
    setShowPublishConfirm(true);
  };

  const handleScheduleChange = (next: ScheduleValue) => {
    setSchedule(next);
    setScheduleErrors({});
    // The schedule is not saved with a draft, so leaving with it set loses it: warn like any edit.
    unsavedChanges.current = true;
  };

  const reportPublishError = (error: any) => {
    const statusCode = error?.networkError?.statusCode || 0;
    if (error?.name === "SchedulePartialFailureError") {
      // The first schedule is already in place; say so, or the editor resubmits into "already
      // trigger is initiated".
      ShowToastError(`${t("schedule_partial_failure")} ${getScheduleErrorText(error.cause, t)}`);
    } else if (error?.graphQLErrors?.length > 0) {
      ShowToastError(getScheduleErrorText(error, t));
    } else {
      ShowToastError(t("api_error_toast"), statusCode);
    }
  };

  const createVod = async (
    pageState,
    pageExist,
    isWorkflow = true,
    plan: ContentPublishPlan | undefined = undefined,
  ) => {
    const newTempData = JSON.parse(JSON.stringify(vodInstance));
    const vodToSend = getVodToSend(
      vodRef,
      newTempData,
      updateTempObj,
      pageState,
      pageExist,
      isFeatured,
      username,
    );
    try {
      setIsLoading(true);
      const createResponse: any = await createVodMutate({
        variables: {
          contenttype: "Vod",
          input: appendBrandNameIfPresent(vodToSend, isShared),
        },
        context: {
          skipBrandNameHeader: isShared,
        },
      });

      const pageUrl = createResponse?.data?.authoring_createContent?.path.substring(
        createResponse?.data?.authoring_createContent?.path.lastIndexOf("/") + 1,
      );
      if (createResponse?.data?.authoring_createContent?.isExist) {
        setOpenPageExistModal(true);
        setPageStatus(pageState);
        setWorkflowStatus(isWorkflow);
      } else {
        setIsDraft(false);
        vodRef.current = {
          ...vodRef.current,
          page: pageUrl,
        };
        setDraftPageURL(pageUrl);
        unsavedChanges.current = false;
        const {
          CommonFields: { page_createdBy, title, description },
        } = vodToSend;
        const workflowObj = {
          createdBy: page_createdBy,
          title,
          description,
          path: createResponse?.data?.authoring_createContent?.path,
          workflow_status: workflowKeys.draft,
          tag_name: capitalizeFirstLetter(ContentType.Vod),
          last_modifiedBy: page_createdBy,
        };
        setWorkflow({ ...workflow, ...workflowObj });
        if (isWorkflow) {
          workflowSubmitRequest(workflowObj, workflowKeys.approve);
        } else {
          ShowToastSuccess(`${t("vod")} ${t("saved_toast")}`);
        }
        // A scheduled publish is saved as DRAFT (see planContentPublish), so it is the plan, not the
        // saved state, that says a publish call must follow.
        if (
          createResponse?.data?.authoring_createContent?.message === "Successfully created!!!" &&
          (pageState === "PUBLISHED" || plan?.isScheduled)
        ) {
          await publishVod(pageUrl, plan);
        }
        setOpenPageExistModal(false);
      }
    } catch (error: any) {
      reportPublishError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (slugValidation.isSubmitCheck) return;
    if (slugValidation.status === "unique") {
      ShowToastSuccess(t("slug_available", { docType: "Vod" }));
    } else if (slugValidation.status === "not_unique") {
      ShowToastError(t("slug_not_unique", { docType: "Vod" }));
    }
  }, [slugValidation.status, slugValidation.isSubmitCheck]);

  const handleSlugChange = (event) => {
    const { value } = event.target;
    // Route through updateField (not a bare setVodInstance) so updateTempObj.current
    // — which getUpdatedVodToSend spreads over vodInstance when building the save
    // payload — also picks up the edited slug, instead of overwriting it with the
    // stale value cached there from the initial fetch.
    updateField({ slug: value }, {});
    // Mirror it onto vodRef too: the structured-data builder reads its page URL
    // from there, and a stale slug would ship a JSON-LD `url`/`@id` pointing at
    // a route the renderer does not serve.
    vodRef.current = { ...vodRef.current, slug: value };
    slugValidation.invalidate();
  };

  const handleSlugBlur = () => {
    slugValidation.validate(vodInstance?.CommonFields?.slug, {
      excludeValue: originalSlugRef.current,
    });
  };

  const currentSlug = vodInstance?.CommonFields?.slug || "";
  const slugField = {
    value: currentSlug,
    onChange: handleSlugChange,
    onBlur: handleSlugBlur,
    // The stored canonical is the source of truth: shown as-is until the slug changes, then rebuilt
    // from the document's own primary brand.
    canonicalUrl: currentSlug
      ? toAbsoluteUrl(
          resolveCanonicalPath({
            slug: currentSlug,
            language: i18n.language,
            tagName: "vod",
            brandId: vodRef?.current?.brand_name,
            stored: vodRef?.current?.storedCanonical,
          }),
        )
      : "",
  };

  // Single source of truth for "is the slug allowed to be saved" — called once
  // from every top-level Save/Publish entry point, BEFORE branching into
  // createVod vs updateVOD. Checking only inside updateVOD left createVod
  // unchecked; once a document is created in this session, isDraft flips to
  // false and every later save routes into updateVOD even without a page
  // reload, so an empty/unverified slug could still reach it. Enforcing here,
  // ahead of that branch, guarantees the slug is always valid by the time
  // either function runs — no route through this component can skip it.
  const guardSlugThenProceed = async (): Promise<boolean> => {
    if (!currentSlug) {
      ShowToastError(t("slug_required"));
      return false;
    }
    const isUnique = await slugValidation.checkNow(currentSlug, {
      excludeValue: originalSlugRef.current,
    });
    if (!isUnique) {
      ShowToastError(t("slug_url_not_unique", { docType: "Vod" }));
      return false;
    }
    // eslint-disable-next-line require-atomic-updates
    originalSlugRef.current = currentSlug;
    return true;
  };

  // Slug is already validated by guardSlugThenProceed before this is ever
  // called (from saveVod/publishButtonHandel, ahead of the createVod/updateVOD
  // branch).
  const updateVOD = async (
    pageState: string,
    isWorkflow,
    props = {},
    event_step = "",
    plan: ContentPublishPlan | undefined = undefined,
  ) => {
    const newTempData = JSON.parse(JSON.stringify(vodInstance));
    const vodToSend = getUpdatedVodToSend(
      newTempData,
      updateTempObj,
      username,
      isFeatured,
      draftPageURL || currentVodData.current,
      pageState,
    );
    try {
      setIsLoading(true);
      const updateResponse: any = await updateVodMutate({
        variables: {
          contenttype: "Vod",
          input: appendBrandNameIfPresent(vodToSend, isShared),
        },
        context: {
          skipBrandNameHeader: isShared,
        },
      });
      const publishedPage =
        draftPageURL || (currentVodData.current ? currentVodData.current : vodRef?.current?.page);
      if (plan?.isScheduled) {
        // Saved as DRAFT/its current state on purpose, so the save-state branches below must not
        // decide this one.
        if (updateResponse?.data?.authoring_updateContent?.message === "Successfully updated!!!") {
          await publishVod(publishedPage, plan);
        }
      } else if (pageState === "DRAFT") {
        if (isWorkflow) {
          workflowSubmitRequest(props, event_step);
        } else {
          ShowToastSuccess(`${t("vod")} ${t("updated_toast")}`);
        }
      } else if (
        updateResponse?.data?.authoring_updateContent?.message === "Successfully updated!!!" &&
        pageState === "PUBLISHED"
      ) {
        await publishVod(publishedPage);
      }
      unsavedChanges.current = false;
      setShowExitWarning(false);
    } catch (error: any) {
      reportPublishError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const saveVod = async (status = false, props = {}, event_step = "") => {
    if (showExitWarning) {
      setShowExitWarning(false);
    }
    // if (fieldValidation(vodRef.current, isError, setIsError)) {
    const pageURL = vodRef?.current?.title?.replace(/[^A-Z0-9]+/gi, "-").toLowerCase();
    updateCurrentInstance(pageURL);
    if (!(await guardSlugThenProceed())) return;
    if (!currentVodData.current && isDraft) {
      createVod("DRAFT", false, status);
    } else {
      updateVOD("DRAFT", status, props, event_step);
    }
    // }
  };

  const publishButtonHandel = async () => {
    if (showExitWarning) {
      setShowExitWarning(false);
    }
    if (fieldValidationForPublish(vodRef.current, isError, setIsError)) {
      const pageURL = currentVodData.current
        ? currentVodData.current
        : vodRef?.current?.title?.replace(/[^A-Z0-9]+/gi, "-").toLowerCase();
      // Validate the schedule BEFORE anything is saved, so a bad time never leaves a half-done
      // publish behind.
      const plan = planContentPublish(
        "Vod",
        schedule,
        vodInstance?.CommonFields?.page_state || pageStatus,
        new Date(),
        initialSchedule.current,
      );
      if (!plan.verdict.valid) {
        const message = capitalizeFirstLetter(t(plan.verdict.messageKey));
        setScheduleErrors({ [plan.verdict.field]: message });
        ShowToastError(message);
        document.getElementById("schedulePublish")?.scrollIntoView?.({
          behavior: "smooth",
          block: "center",
        });
        return;
      }
      setScheduleErrors({});
      updateCurrentInstance(pageURL);
      if (!(await guardSlugThenProceed())) return;
      if (!currentVodData.current && isDraft) {
        createVod(plan.saveState, false, false, plan);
      } else {
        updateVOD(plan.saveState, false, {}, "", plan);
      }
    }
  };

  //Functions to handle Page Exist modal
  const pageExistNoButtonHandle = () => {
    setOpenPageExistModal(false);
  };

  const pageExistYesButtonHandle = () => {
    setOpenPageExistModal(false);
    if (pageStatus.toLowerCase() === "DRAFT") {
      createVod("DRAFT", true, workflowStatus);
    } else if (pageStatus.toLowerCase() === "PUBLISHED") {
      createVod("PUBLISHED", true);
    }
  };

  const handleSelectedImage = (image) => {
    vodRef.current = { ...vodRef.current, Thumbnail: image?.Thumbnail };
    const structureData = updateStructureData(vodRef);
    setVodInstance((prevState) => {
      return {
        ...prevState,
        CommonFields: {
          ...prevState.CommonFields,
          structure_data: mergeAutoStructureData(
            prevState?.CommonFields?.structure_data,
            structureData,
          ),
        },
      };
    });
    unsavedChanges.current = true;
  };

  /**
   * update video
   * @param video object
   */
  const shouldAllowAI = canAccessAction("Content", "Vod", "AI");

  const handleSelectedVideo = (video) => {
    if (shouldAllowAI) {
      setOpenAImodal(true);
    } else {
      setOpenAImodal(false);
    }
    aiContentRef.current = {
      collectionId: video?.uuid,
      assetId: video?.bitStreamId,
    };
    const videoObject = {
      page: draftPageURL || "",
      DsapceVideoUrl: toRelativeVideoPath(video?.Url),
      Thumbnail: video?.Thumbnail,
      imageSocialShare: video?.Thumbnail,
      notification_image: video?.Thumbnail,
      // The DAM already returns the Brightcove id; it used to be dropped here,
      // which left ObjectFields.VodVideoId permanently "". That is what the
      // JSON-LD embedUrl needs — and what the renderer's Brightcove player is
      // gated on (ReactBrightCovePlayer renders nothing without it).
      VodVideoId: video?.videoId || "",
      // Brightcove's duration in ms, for the JSON-LD `duration`. Held on the ref
      // ONLY — it is deliberately absent from ObjectFields because the authoring
      // backend whitelists that map and rejects the whole save on an unknown key
      // ("Unknown fields are present! [VodDuration]"). The generated markup is
      // what persists it: once written, `carriedOverVideoFields` in
      // structuredData/vod.ts carries `duration` across later saves, exactly as
      // it already does for `embedUrl`. Empty until KIWI-6410 fills the DAM
      // field; add it to ObjectFields only once the backend accepts it.
      VodDuration: video?.duration || "",
      original_image: {
        original_image_relative_path: video?.original_image_relative_path || "",
        ext: video?.ext || "",
      },
    };
    vodRef.current = {
      ...vodRef.current,
      ...videoObject,
    };
    const structureData = updateStructureData(vodRef);
    setVodInstance((prevState) => {
      return {
        ...prevState,
        CommonFields: {
          ...prevState.CommonFields,
          page: draftPageURL || "",
          imageSocialShare: video?.Thumbnail,
          notification_image: video?.Thumbnail,
          structure_data: mergeAutoStructureData(
            prevState?.CommonFields?.structure_data,
            structureData,
          ),
        },
        ObjectFields: {
          ...prevState.ObjectFields,
          DsapceVideoUrl: toRelativeVideoPath(video?.Url),
          Thumbnail: video?.Thumbnail,
          VodVideoId: video?.videoId || "",
          original_image: {
            original_image_relative_path: video?.original_image_relative_path || "",
            ext: video?.ext || "",
          },
        },
      };
    });
    unsavedChanges.current = true;
    setIsError({ ...isError, DsapceVideoUrl: false });
  };
  const handleAIContent = async () => {
    setOpenAImodal(false);
    if (aiContentRef.current?.collectionId && aiContentRef.current?.assetId) {
      const result = await fetchContent(
        aiContentRef.current?.collectionId,
        aiContentRef.current?.assetId,
      );
      if (result && vodRef?.current) {
        const titleMaxLength = getTitleMax("vod");
        const {
          summary,
          teaser,
          label,
          seo_keywords = [],
        } = result.data.authoring_getAssetMetadata;
        let tagsArray: any = [...(label ?? [])];
        tagsArray = [...tagsArray, "Video Card"];
        vodRef.current = {
          ...vodRef.current,
          Tags: removeVODDuplicateTag(tagsArray),
          tagsSocialShare:
            seo_keywords?.length > 0
              ? removeVODDuplicateTag([...seo_keywords, ...tagsArray])
              : removeVODDuplicateTag(tagsArray),
          title: teaser?.slice(0, titleMaxLength) || "",
          description: summary?.slice(0, 1000) || "",
          titleSocialShare: teaser?.slice(0, 120) || "",
          descriptionSocialShare: summary?.slice(0, 1000) || "",
        };
        setVodInstance((prevState) => {
          return {
            ...prevState,
            CommonFields: {
              ...prevState.CommonFields,
              tags: vodRef.current.Tags,
              tagsSocialShare: vodRef.current.tagsSocialShare,
              title: vodRef.current.title,
              description: vodRef.current.description,
              titleSocialShare: vodRef.current.titleSocialShare,
              descriptionSocialShare: vodRef.current.descriptionSocialShare,
            },
          };
        });
      }
    }
  };

  const handleNoButton = () => {
    setOpenAImodal(false);
  };

  const toggleGallery = (toggleState) => {
    setGalleryState(toggleState);
  };

  const showGallery = (gType) => {
    window.scrollTo(0, 0);
    galleryType.current = gType;
    setGalleryState(true);
  };

  const handleTagOnChange = (event, category = "") => {
    let tagsArray: any = vodRef?.current?.Tags
      ? [...(vodRef?.current?.Tags || [])]
      : [...tagRef.current];

    let tagsCategory: any = vodRef?.current?.tag_sm
      ? [...(vodRef?.current?.tag_sm || [])]
      : [...tagCategory];

    if (event.target.checked) {
      tagsArray = [...tagsArray, event?.target?.value];
      tagsCategory = [
        ...tagsCategory,
        buildTagSmEntry(category, event.target.value, event.target.tagValue),
      ];

      if (tagsArray?.length > 100) {
        ShowToastError("You cannot select more than 100 tags.");
        return false;
      }
    } else {
      tagsArray.splice(tagsArray.indexOf(event.target.value), 1);
      tagsCategory.splice(findTagSmEntryIndex(tagsCategory, category, event.target.value), 1);
    }
    // Dedupe: the same (category, name) pair can otherwise be added twice if this fires
    // more than once for the same toggle (e.g. a fast double-click).
    tagsCategory = dedupeTagSmEntries(tagsCategory);
    setTagCategory(tagsCategory);
    tagRef.current = tagsArray;
    vodRef.current = {
      ...vodRef.current,
      Tags: removeVODDuplicateTag(tagsArray),
      tagsSocialShare: removeVODDuplicateTag(tagsArray),
      tag_sm: tagsCategory,
    };
    setVodInstance((prevState) => {
      return {
        ...prevState,
        CommonFields: {
          ...prevState.CommonFields,
          tags: removeVODDuplicateTag(tagsArray),
          tagsSocialShare: removeVODDuplicateTag(tagsArray),
          tag_sm: tagsCategory,
        },
      };
    });
    unsavedChanges.current = true;
    setIsError({ ...isError, Tags: false });
  };
  const handleSocialShareTagChange = (_, value) => {
    const result = value.filter((str) => str.trim()?.length !== 0);
    const updatedtags = result.filter((c, index) => {
      return result.indexOf(c) === index;
    });
    vodRef.current = {
      ...vodRef.current,
      tagsSocialShare: removeVODDuplicateTag(updatedtags),
    };
    setVodInstance((prevState) => {
      return {
        ...prevState,
        CommonFields: {
          ...prevState.CommonFields,
          tagsSocialShare: removeVODDuplicateTag(updatedtags),
        },
      };
    });
  };
  const handleSocialShareImageChange = (data) => {
    setVodInstance((prevState) => {
      return {
        ...prevState,
        CommonFields: {
          ...prevState.CommonFields,
          imageSocialShare: data?.original_image?.Thumbnail || "",
        },
      };
    });
    vodRef.current = {
      ...vodRef.current,
      imageSocialShare: data?.original_image?.Thumbnail || "",
    };
    unsavedChanges.current = true;
  };
  const handleChange = (event) => {
    const { name } = event.target;
    const key = `${name}SocialShare`;
    vodRef.current = { ...vodRef.current, [name]: event?.target?.value, [key]: event.target.value };
    const structureData = updateStructureData(vodRef);
    unsavedChanges.current = true;
    if (
      (name === "description" && vodRef?.current?.description === event?.target?.value) ||
      (name === "title" && vodRef?.current?.title === event?.target?.value)
    ) {
      setVodInstance((prevState) => {
        return {
          ...prevState,
          CommonFields: {
            ...prevState.CommonFields,
            [name]: event?.target?.value,
            [key]: event.target.value,
            structure_data: mergeAutoStructureData(
              prevState?.CommonFields?.structure_data,
              structureData,
            ),
          },
        };
      });
      setIsError({ ...isError, [name]: false });
    }
  };
  const handleSocialShareChange = (event) => {
    const { name } = event.target;
    vodRef.current = { ...vodRef.current, [name]: event?.target?.value };
    if (
      (name === "titleSocialShare" && vodRef?.current?.titleSocialShare === event?.target?.value) ||
      (name === "descriptionSocialShare" &&
        vodRef?.current?.descriptionSocialShare === event?.target?.value)
    ) {
      setVodInstance((prevState) => {
        return {
          ...prevState,
          CommonFields: {
            ...prevState.CommonFields,
            [name]: event?.target?.value,
          },
        };
      });
    }
    unsavedChanges.current = true;
  };
  const handleOnchangeItem = (event) => {
    const { name } = event.target;
    setVodInstance((prevState) => {
      return {
        ...prevState,
        CommonFields: {
          ...prevState.CommonFields,
          [name]: event?.target?.value,
        },
      };
    });

    unsavedChanges.current = true;
  };
  const handleAnalyticsSeoChange = (event, keyName) => {
    vodRef.current = { ...vodRef.current, [keyName]: event?.target?.checked };
    setVodInstance((prevState) => {
      return {
        ...prevState,
        CommonFields: {
          ...prevState.CommonFields,
          [keyName]: event?.target?.checked,
        },
      };
    });
  };

  const handleAuthorToggleChange = (event, keyName) => {
    const checked = event?.target?.checked;
    updateField({}, { [keyName]: checked });
    unsavedChanges.current = true;
  };

  const handleStrucutreDataChange = (structureData) => {
    setVodInstance((prevState) => {
      return {
        ...prevState,
        CommonFields: {
          ...prevState.CommonFields,
          structure_data: JSON.stringify(structureData),
        },
      };
    });
  };

  const createComment = () => {
    const currentLanguage = getCurrentLang();
    const createCommentRequest = {
      document_path: `/content/documents/hclplatformx/${currentLanguage}/vod/${currentVodData.current}`,
      status: false,
      document_type: "Vod",
      created_by: username,
      last_modified_by: username,
      reviewer_comments: [comments],
    };

    return commentsApi.createOrUpdateComment({
      input: createCommentRequest,
    });
  };

  const backToVodList = () => {
    if (unsavedChanges.current === true) {
      setShowExitWarning(true);
    } else {
      navigate("/content/vod");
    }
  };

  const onBackButtonEvent = (e) => {
    e.preventDefault();
    if (unsavedChanges.current) {
      backToVodList();
    } else {
      // eslint-disable-next-line no-restricted-globals
      window.history.pushState(null, "", window.location.pathname + location?.search);
      backToVodList();
    }
  };

  const pageExistCrossButtonHandle = () => {
    setOpenPageExistModal(false);
    // eslint-disable-next-line no-restricted-globals
    window.history.pushState(null, "", window.location.pathname + location?.search);
    window.addEventListener("popstate", onBackButtonEvent);
  };

  const onUploadClick = (type) => {
    showGallery(type);
  };

  useEffect(() => {
    if (Object.keys(vodInstance).length === 0) {
      setVodInstance(getNewVod(username, vodRef));
    }
    const fetchWorkflowDetails = async () => {
      setIsLoading(true);
      if (currentVodData.current === "" && Object.keys(currentContent).length === 0) {
        await getWorkflowDetails(
          role,
          login_user_id,
          setWorkflow,
          capitalizeFirstLetter(ContentType.Vod),
        );
      }
      setIsLoading(false);
    };
    fetchWorkflowDetails();
  }, []);

  useEffect(() => {
    if (Object.keys(currentContent).length > 0) {
      const tempCurrentContent = { ...currentContent };
      const tempCurrentContentCommonFields = { ...(tempCurrentContent.CommonFields || {}) };
      setDraftPageURL(tempCurrentContentCommonFields?.draftPageURL);
      setIsDraft(tempCurrentContentCommonFields?.isDraft);
      setWorkflow(tempCurrentContentCommonFields?.workflow);

      delete tempCurrentContentCommonFields?.isDraft;
      delete tempCurrentContentCommonFields?.contentType;
      delete tempCurrentContentCommonFields?.draftPageURL;
      delete tempCurrentContentCommonFields?.workflow;
      // Restoring from a Preview round-trip (not a fresh fetch) — treat the
      // restored slug as the new baseline so re-saving it unchanged doesn't
      // trigger a stale/empty excludeValue and a false "not unique" result.
      originalSlugRef.current = tempCurrentContentCommonFields?.slug || "";
      const existingSchedule = scheduleFromTriggers(tempCurrentContent.scheduleTriggers);
      initialSchedule.current = existingSchedule.initial;
      setSchedule(existingSchedule.initial);
      setPendingSchedule(existingSchedule.pending);
      setVodInstance((prevState) => {
        return {
          ...prevState,
          CommonFields: {
            ...prevState.CommonFields,
            ...tempCurrentContentCommonFields,
          },
          ObjectFields: {
            ...prevState?.ObjectFields,
            ...tempCurrentContent.ObjectFields,
          },
        };
      });
    }
  }, [currentContent]);

  useEffect(() => {
    const fetchVodContent = async () => {
      try {
        setIsLoading(true);
        const {
          data: { authoring_getCmsContentByPath: fetchVodResponse },
        } = await fetchVodQuery({
          variables: {
            contentType: "Vod",
            path: currentVodData.current,
          },
        });
        const {
          path,
          workflow_status,
          stages,
          tag_name,
          last_modifiedBy,
          createdBy,
          title,
          task_status,
          user_id,
          user_name,
          page,
          description,
          tags,
          settingsProperties,
          is_featured,
          DsapceVideoUrl,
          Thumbnail,
          original_image,
          published_images,
          show_author,
          analytics_enable,
          seo_enable,
          structure_data,
          notification,
          notification_title,
          notification_desc,
          notification_channel,
          notification_topics,
          notification_image,
          content_behind_rendering,
          tag_sm,
          slug,
          VodVideoId,
          creationDate,
        } = fetchVodResponse;
        const { socialog_title, socialog_description, socialog_image, keywords } =
          settingsProperties;
        const updatedCommonFields = {
          title,
          description,
          tags,
          tag_sm,
          settings: settingsProperties,
          is_featured,
          page,
          titleSocialShare: socialog_title,
          descriptionSocialShare: socialog_description,
          imageSocialShare: socialog_image,
          tagsSocialShare: [...keywords],
          analytics_enable,
          seo_enable,
          structure_data:
            structure_data ||
            JSON.stringify(
              updateStructureData({
                current: {
                  DsapceVideoUrl,
                  Thumbnail,
                  title,
                  description,
                  slug,
                  page,
                  creationDate,
                  original_image,
                  VodVideoId,
                },
              }),
            ),
          notification,
          notification_title: notification_title || title,
          notification_desc: notification_desc || description,
          notification_channel,
          notification_topics,
          notification_image: notification_image || socialog_image,
          content_behind_rendering,
          slug,
        };
        originalSlugRef.current = slug || "";
        const updatedObjectFields = {
          DsapceVideoUrl: DsapceVideoUrl || "",
          original_image: original_image || {},
          published_images: published_images || [],
          show_author: show_author ?? false,
          VodVideoId: VodVideoId || "",
        };
        setVodInstance((prevState) => {
          return {
            ...prevState,
            CommonFields: {
              ...prevState.CommonFields,
              ...updatedCommonFields,
            },
            ObjectFields: {
              ...prevState.ObjectFields,
              ...updatedObjectFields,
            },
          };
        });
        setTagCategory(tag_sm);
        setDraftPageURL(page);
        const existingSchedule = scheduleFromTriggers(fetchVodResponse);
        initialSchedule.current = existingSchedule.initial;
        setSchedule(existingSchedule.initial);
        setPendingSchedule(existingSchedule.pending);
        const videoObj = {
          page,
          slug,
          creationDate,
          // Carried so the builder can preserve a previously emitted embedUrl
          // while ObjectFields.VodVideoId is unavailable (KIWI-6283).
          structure_data,
          VodVideoId: VodVideoId || "",
          DsapceVideoUrl: DsapceVideoUrl || "",
          original_image: original_image || {},
          published_images: published_images || [],
          title,
          description,
          Tags: removeVODDuplicateTag(tags),
          tag_sm,
          is_featured,
          titleSocialShare: socialog_title,
          descriptionSocialShare: socialog_description,
          imageSocialShare: socialog_image,
          tagsSocialShare: [...keywords],
          analytics_enable,
          seo_enable,
          // The document's own primary brand, shared brands and stored canonical (KIWI-6401).
          ...brandFieldsFromDoc(fetchVodResponse),
        };
        vodRef.current = {
          ...videoObj,
        };
        updateField(updatedCommonFields, updatedObjectFields);
        setIsFeatured(is_featured);

        setWorkflow({
          path,
          workflow_status,
          stages,
          tag_name,
          last_modifiedBy,
          createdBy,
          role,
          title,
          enable: stages?.length > 0 ? true : false,
          login_user_id,
          task_status,
          task_user_id: user_id,
          task_user_name: user_name,
          description,
        });
      } catch (error: any) {
        const statusCode = error?.networkError?.statusCode || 0;
        ShowToastError("Some thing went wrong", statusCode);
      } finally {
        setIsLoading(false);
        setVodTagsHydrated(true);
      }
    };
    if (
      currentVodData.current &&
      unsavedChanges.current !== true &&
      Object.keys(currentContent).length === 0
    ) {
      fetchVodContent();
    }
    if (Object.keys(tagData).length === 0) {
      runFetchTagList({
        variables: { start: 0, rows: 1000 },
      })
        .then((res) => {
          if (res?.data?.authoring_getTagsList) {
            setTagData(res?.data?.authoring_getTagsList);
          }
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.log(JSON.stringify(err, null, 2));
          ShowToastError(t("api_error_toast"));
        });
    }
  }, []);

  useEffect(() => {
    if (!currentVodData.current && tagData?.length > 0) {
      handleTagOnChange({
        target: {
          checked: true,
          value: "Video Card",
        },
      });
    }
  }, [tagData?.length]);

  const isInViewport = (element) => {
    const mainElement = document.querySelector(`#${element}`);
    if (mainElement) {
      const rect = mainElement.getBoundingClientRect();
      return rect.top <= window.pageYOffset + window.innerHeight && rect.top >= window.pageYOffset;
    }
    return false;
  };

  const scrollHandler = () => {
    if (scrollDebounceRef.current) {
      clearTimeout(scrollDebounceRef.current);
    }
    const timeOutId = setTimeout(() => {
      const active = icons.find((i) => isInViewport(i.id));
      if (active && active.tooltip !== parentToolTip) {
        setParentToolTip(active.tooltip);
      }
    }, 100);
    scrollDebounceRef.current = timeOutId;
  };
  useEffect(() => {
    const dataHolder = document.getElementById("scrollableDiv");
    dataHolder?.addEventListener("scroll", scrollHandler);
    return () => {
      window.removeEventListener("scroll", scrollHandler);
    };
  }, []);

  useEffect(() => {
    const {
      CommonFields: { title = "", tags = [] } = {},
      ObjectFields: { DsapceVideoUrl = "", original_image = {} } = {},
    } = vodInstance || {};
    if (
      title === "" ||
      tags?.length === 0 ||
      DsapceVideoUrl === "" ||
      !original_image?.original_image_relative_path
    ) {
      setPreviewButton(true);
    } else if (!currentVodData?.current && isDraft) {
      setPreviewTooltip(false);
    } else {
      setPreviewButton(false);
    }
  }, [vodInstance, isDraft]);
  // Self-heals tag_sm for legacy content whose `Tags` flat list has a name with no matching
  // tag_sm entry at all (saved before tag_sm existed) - by looking that name up in the master
  // tag taxonomy. Deliberately leaves every tag that ALREADY has a tag_sm entry untouched:
  // re-deriving category for those too (the old behavior) both duplicated entries when the
  // master taxonomy listed the same tag under a category twice, and could lose entries that
  // don't exist in the taxonomy at all, e.g. the "Content Type" tag.
  useEffect(() => {
    return syncTagSm(vodRef?.current?.tag_sm, tagData, vodRef?.current?.Tags, (tagsCategory) => {
      vodRef.current = { ...vodRef.current, tag_sm: tagsCategory };
      setTagCategory(tagsCategory);
      setVodInstance((prevState) => {
        return {
          ...prevState,
          CommonFields: {
            ...prevState.CommonFields,
            tag_sm: tagsCategory,
          },
        };
      });
    });
  }, [tagData, vodRef?.current?.Tags]);
  return (
    <>
      {isLoading && <Loader />}
      <Box>
        <Box>
          {/* Top Section start */}
          <CreateHeader
            hasPreviewButton={previewButton}
            handelPreview={handelPreview}
            createText={currentVodData.current ? `${t("edit")} ${t("vod")}` : t("create_vod")}
            handleReturn={backToVodList}
            hasPublishButton={false}
            handleSaveOrPublish={saveVod}
            publishText={t("publish")}
            saveText={t("save_as_draft")}
            previewText={t("preview")}
            toolTipText={previewTooltip ? t("preview_tooltip") : t("preview_tooltip1")}
            saveVariant='secondaryButton'
            handlePublish={publishButtonHandel}
            category={CATEGORY_CONTENT}
            subCategory={ContentType.Vod}
            workflow={workflow}
            createComment={createComment}
            setEnableWorkflowHistory={setEnableWorkflowHistory}
            setIsFeatured={setIsFeatured}
            isFeatured={isFeatured}
            publishDisabled={isLoading}
          />
          {/* Top Section End */}
          <Divider></Divider>
        </Box>
        <Box
          sx={{
            position: "relative",
            height: {
              sm: "calc(100vh - 125px)",
              xs: "calc(100vh - 45px)",
            },
            overflowY: "scroll",
            overflowX: "hidden",
          }}
          id='scrollableDiv'>
          {!enableWorkflowHistory && (
            <Box
              sx={{
                position: "fixed",
                top: "25%",
                right: { sm: "5px", xs: 0 },
                zIndex: 1000,
              }}>
              <ContentPageScroll
                icons={icons}
                parentToolTip={parentToolTip}
                scrollToView={""} // scrollToView={scrollToView}
              />
            </Box>
          )}
          {enableWorkflowHistory ? (
            <WorkflowHistory
              workflow={workflow}
              setEnableWorkflowHistory={setEnableWorkflowHistory}
            />
          ) : (
            <>
              {/* Video Start */}
              <Box id='VideoAndThumbnail' className={classes.mainStyleWrapper}>
                <CommentWrapper elementId='2'>
                  <CommonBoxWithNumber
                    number='01'
                    title={t("video_subtitle")}
                    titleVarient='h5bold'
                    subTitleVarient='p4regular'
                    subTitle={t("subhead")}>
                    <Grid container>
                      <Grid item xs={12} sm={5} md={5} lg={5} className='leftFiled'>
                        <TitleSubTitle
                          title={t("video_title")}
                          subTitle={t("video_subtitle")}
                          titleVariant='h6medium'
                          subTitleVariant='p4regular'
                        />
                      </Grid>
                      <Grid item xs={12} sm={7} md={7} lg={7} className='textFiled' id='Video'>
                        <ChooseVideoTray
                          ifVideoUrl={resolveVideoUrl(
                            vodRef.current?.DsapceVideoUrl,
                            AUTH_INFO?.dspaceUri,
                          )}
                          brightcoveVideoId={vodRef.current?.VodVideoId}
                          poster={vodRef.current?.Thumbnail}
                          onUploadClick={onUploadClick}
                        />
                        {isError["DsapceVideoUrl"] && (
                          <ErrorMessageHandle error={{ message: "Field is Required" }} />
                        )}
                      </Grid>

                      <Grid item xs={12} sm={5} md={5} lg={5} className='leftFiledLast'>
                        <TitleSubTitle
                          title={t("vod_thumbnail_title")}
                          subTitle={t("vod_thumbnail_subtitle")}
                          titleVariant='h6medium'
                          subTitleVariant='p4regular'
                        />
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sm={7}
                        md={7}
                        lg={7}
                        className='textFiledLast'
                        id='Thumbnail'>
                        <XImageRender
                          callBack={updateImageField}
                          editData={{
                            original_image: vodInstance?.ObjectFields?.original_image || {},
                            published_images: vodInstance?.ObjectFields?.published_images || [],
                          }}
                        />
                        {isError["Thumbnail"] && (
                          <ErrorMessageHandle error={{ message: "Field is Required" }} />
                        )}
                      </Grid>
                    </Grid>
                  </CommonBoxWithNumber>
                </CommentWrapper>
              </Box>
              {/* Video End */}
              {/* Title Desc start */}
              <Box id='titleDescription' className={classes.mainStyleWrapper}>
                <CommentWrapper elementId='1'>
                  <CommonBoxWithNumber
                    number='02'
                    title={t("title_head")}
                    titleVarient='h5bold'
                    subTitleVarient='p4regular'
                    subTitle={t("subhead")}>
                    <Grid container>
                      <Grid item xs={12} sm={5} md={5} lg={5} className='leftFiled'>
                        <TitleSubTitle
                          title={t("vod_title")}
                          subTitle={t("vod_subtitle")}
                          titleVariant='h6medium'
                          subTitleVariant='p4regular'
                        />
                      </Grid>
                      <Grid item xs={12} sm={7} md={7} lg={7} className='textFiled' id='Title'>
                        <TextBox
                          name='title'
                          placeHolder={t("vod_title_placeholder")}
                          handleChange={handleChange}
                          maxCharLength={getTitleMax("vod")}
                          state={vodRef?.current?.title || ""}
                          error={isError["title"]}
                        />
                      </Grid>
                      <Grid item xs={12} sm={5} md={5} lg={5} className='leftFiledLast'>
                        <TitleSubTitle
                          title={t("vod_desciption")}
                          subTitle={t("vod_subdes")}
                          titleVariant='h6medium'
                          subTitleVariant='p4regular'
                        />
                      </Grid>
                      <Grid item xs={12} sm={7} md={7} lg={7} className='textFiledLast'>
                        <AutoTextArea
                          name='description'
                          placeHolder={t("vod_desciption_placeholder")}
                          handleChange={handleChange}
                          maxCharLength={1000}
                          state={vodRef?.current?.description || ""}
                        />
                      </Grid>
                    </Grid>
                  </CommonBoxWithNumber>
                </CommentWrapper>
              </Box>

              {/* Title Desc End */}

              {/* Tag Start */}
              <Box id='tags' className={classes.mainStyleWrapper}>
                <CommonBoxWithNumber
                  number='03'
                  title={t("choose_tags")}
                  titleVarient='h5bold'
                  subTitleVarient='p4regular'
                  subTitle={t("subhead")}>
                  <TagSelectorPanel
                    isEdit={Boolean(currentVodData.current)}
                    tagData={tagData}
                    selectedTag={vodInstance?.CommonFields?.tags}
                    selectedTagSm={vodInstance?.CommonFields?.tag_sm}
                    isContentReady={vodTagsHydrated}
                    subCategory='Video Card'
                    handleTagOnChange={handleTagOnChange}
                  />
                  {isError["Tags"] && (
                    <ErrorMessageHandle error={{ message: "Field is Required" }} />
                  )}
                </CommonBoxWithNumber>
              </Box>
              {/* Tag End */}
              {/* Social Share Start */}
              <SocialShare
                titleSubTitles={socialShareTitleSubTitles(t)}
                state={vodInstance?.CommonFields}
                handleOnBlur={handleSocialShareChange}
                handleChange={() => {}}
                handleTagChange={handleSocialShareTagChange}
                handleImageChange={handleSocialShareImageChange}
              />
              {/* Social Share End */}
              {/* Analytics Start */}
              <Analytics
                titleSubTitles={analyticsTitleSubTitles(t)}
                state={vodInstance?.CommonFields}
                handleChange={handleAnalyticsSeoChange}
              />
              {/* Analytics End */}
              {/* SEO Start */}
              <Seo
                titleSubTitles={seoTitleSubTitles(t)}
                state={vodInstance?.CommonFields}
                handleChange={handleAnalyticsSeoChange}
                handleStrucutreDataChange={handleStrucutreDataChange}
                slugField={slugField}
              />
              {/* SEO End */}
              {/* Schedule Start. Numbered 07, the one slot the fixed numbering left free, so no
                  other section has to be renumbered. */}
              <Box className={classes.mainStyleWrapper}>
                <SchedulePublishUnpublish
                  variant='section'
                  sectionNumber='07'
                  value={schedule}
                  onChange={handleScheduleChange}
                  errors={scheduleErrors}
                  pending={pendingSchedule}
                />
              </Box>
              {/* Schedule End */}
              {hasNotificationAccess ? (
                <FCMNotification
                  titleSubTitles={notificationTitleSubTitles()}
                  state={vodInstance}
                  setState={setVodInstance}
                  unsavedChanges={unsavedChanges}
                  contentRef={vodRef}
                  sectionNumber='08'
                  handleOnBlur={handleOnchangeItem}
                  handleChange={handleAnalyticsSeoChange}
                />
              ) : null}
              {hasContentStatusAccess ? (
                <ContentStatus
                  titleSubTitles={contentStatusTitleSubTitles()}
                  state={vodInstance}
                  setState={setVodInstance}
                  unsavedChanges={unsavedChanges}
                  contentRef={vodRef}
                  sectionNumber={hasNotificationAccess ? "09" : "08"}
                />
              ) : null}
              <AuthorToggle
                titleSubTitles={authorTitleSubTitles(
                  t,
                  hasNotificationAccess,
                  hasContentStatusAccess,
                )}
                state={vodInstance?.ObjectFields}
                handleChange={handleAuthorToggleChange}
              />
            </>
          )}
        </Box>
      </Box>

      {/* video update */}
      {galleryState && (
        <DamContentGallery
          assetType={"Video"}
          dialogOpen={galleryState}
          toggleGallery={toggleGallery}
          handleImageSelected={handleSelectedImage}
          handleSelectedVideo={handleSelectedVideo}
        />
      )}

      {/* after publish dialog will show */}
      {(showPublishConfirm || showWorkflowSubmit) && (
        <CommonPlateformXDialog
          isDialogOpen={showPublishConfirm || showWorkflowSubmit}
          title={t("congratulations")}
          subTitle={
            showPublishConfirm
              ? `${t("your")} ${t("vod")} ${scheduleConfirmation || t("publish_popup_message")}`
              : t("requested_action")
          }
          confirmButtonText={t("go_to_listing")}
          confirmButtonHandle={() => navigate("/content/vod")}
          closeButtonHandle={() => navigate("/content/vod")}
          modalType='publish'
        />
      )}

      {showExitWarning && (
        <CommonPlateformXDialog
          isDialogOpen={showExitWarning}
          title={t("save_warn_title")}
          subTitle={t("save_warn_subtitle")}
          closeButtonText={t("stay_here")}
          confirmButtonText={t("take_me_out")}
          closeButtonHandle={() => {
            setShowExitWarning(false);
          }}
          confirmButtonHandle={exitWithoutSave}
          modalType='unsavedChanges'
        />
      )}

      {openPageExistModal && (
        <CommonPlateformXDialog
          isDialogOpen={openPageExistModal}
          title={`${t("vod")} ${t("duplicate_exists")}`}
          subTitle={t("conformation")}
          closeButtonText={t("no")}
          confirmButtonText={t("yes")}
          closeButtonHandle={pageExistNoButtonHandle}
          confirmButtonHandle={pageExistYesButtonHandle}
          crossButtonHandle={pageExistCrossButtonHandle}
          modalType=''
        />
      )}
      <CommentListPanel />
      <Box>
        {openAImodal && (
          <CommonPlateformXDialog
            isDialogOpen={true}
            title={t("generate_video_content")}
            subTitle={t("ai_title_description_tags")}
            closeButtonText={t("no")}
            confirmButtonText={t("yes")}
            closeButtonHandle={handleNoButton}
            confirmButtonHandle={handleAIContent}
            modalType='generateContent'
            notification={t("overwrite_warning")}
          />
        )}
        {loading && <AILoader />}
      </Box>
    </>
  );
};
